import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar as CalendarIcon, RefreshCw, Check, Building2, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { useFilters } from "../context/FiltersContext";
import { presets } from "../lib/dates";
import { apiClearCache } from "../lib/api";
import { cn } from "../lib/utils";
import { toast } from "sonner";

export const Topbar = ({ title, subtitle }) => {
  const {
    preset, dateFrom, dateTo, applyPreset, applyCustom, triggerRefresh,
    companies, selectedCompanyIds, toggleCompany, selectAllCompanies,
  } = useFilters();
  const [refreshing, setRefreshing] = useState(false);
  const [customFrom, setCustomFrom] = useState(new Date(dateFrom));
  const [customTo, setCustomTo] = useState(new Date(dateTo));
  const [open, setOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);

  const all = presets();

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await apiClearCache();
      triggerRefresh();
      toast.success("Datos actualizados desde Odoo");
    } catch {
      toast.error("Error al refrescar");
    } finally {
      setRefreshing(false);
    }
  };

  const currentDateLabel =
    preset === "custom" ? `${dateFrom} — ${dateTo}` : all[preset]?.label || "Personalizado";

  const allSelected = companies.length > 0 && selectedCompanyIds.length === companies.length;
  const companyLabel = !companies.length
    ? "Empresa"
    : allSelected
    ? `Todas (${companies.length})`
    : selectedCompanyIds.length === 1
    ? companies.find((c) => c.id === selectedCompanyIds[0])?.name?.slice(0, 28) || "1 empresa"
    : `${selectedCompanyIds.length} empresas`;

  return (
    <header
      className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-4 flex-wrap"
      data-testid="topbar"
    >
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight" data-testid="topbar-title">
          {title}
        </h1>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Company selector */}
        <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9 border-slate-200"
              data-testid="company-selector-button"
            >
              <Building2 className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium max-w-[180px] truncate">{companyLabel}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[280px] p-0" data-testid="company-selector-popover">
            <div className="p-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500">
                Empresas
              </span>
              <button
                onClick={selectAllCompanies}
                data-testid="select-all-companies"
                className="text-xs text-[#002FA7] hover:underline"
              >
                Todas
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {companies.map((c) => {
                const checked = selectedCompanyIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    data-testid={`company-toggle-${c.id}`}
                    onClick={() => toggleCompany(c.id)}
                    className="w-full flex items-center gap-2 text-left text-xs px-3 py-2 hover:bg-slate-50 transition-colors duration-150"
                  >
                    <span
                      className={cn(
                        "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                        checked ? "bg-[#002FA7] border-[#002FA7] text-white" : "border-slate-300"
                      )}
                    >
                      {checked && <Check className="w-3 h-3" />}
                    </span>
                    <span className="truncate text-slate-700">{c.name}</span>
                  </button>
                );
              })}
              {!companies.length && (
                <div className="px-3 py-4 text-xs text-slate-400 text-center">Cargando...</div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Date range */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9 border-slate-200"
              data-testid="date-range-button"
            >
              <CalendarIcon className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium">{currentDateLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[520px] p-0" data-testid="date-range-popover">
            <div className="grid grid-cols-[160px,1fr] border-b border-slate-200">
              <div className="border-r border-slate-200 py-2">
                {Object.entries(all).map(([k, v]) => (
                  <button
                    key={k}
                    data-testid={`preset-${k}`}
                    onClick={() => {
                      applyPreset(k);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full text-left text-xs px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors duration-150",
                      preset === k && "bg-slate-50 font-medium text-[#002FA7]"
                    )}
                  >
                    {v.label}
                    {preset === k && <Check className="w-3.5 h-3.5" />}
                  </button>
                ))}
              </div>
              <div className="p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500 mb-2">
                  Rango personalizado
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[11px] text-slate-500 mb-1">Desde</div>
                    <Calendar
                      mode="single"
                      selected={customFrom}
                      onSelect={(d) => d && setCustomFrom(d)}
                      data-testid="custom-date-from"
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 mb-1">Hasta</div>
                    <Calendar
                      mode="single"
                      selected={customTo}
                      onSelect={(d) => d && setCustomTo(d)}
                      data-testid="custom-date-to"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-[#002FA7] hover:bg-[#002280]"
                    data-testid="apply-custom-date"
                    onClick={() => {
                      const f = customFrom.toISOString().slice(0, 10);
                      const t = customTo.toISOString().slice(0, 10);
                      applyCustom(f, t);
                      setOpen(false);
                    }}
                  >
                    Aplicar
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="default"
          size="sm"
          className="gap-2 h-9 bg-[#002FA7] hover:bg-[#002280]"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="refresh-button"
        >
          <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          <span className="text-xs font-medium">Actualizar</span>
        </Button>
      </div>
    </header>
  );
};
